import type { FC } from 'react';
import { Button, CodeSnippet, InlineLoading, InlineNotification, Stack, Tag, Tile } from '@carbon/react';
import { ArrowRight, Launch } from '@carbon/icons-react';
import { CreditCard, Receipt, Time, Trophy, Wifi } from '@carbon/pictograms-react';

import { RECEBEDOR } from '@maestra/core/constants/checkout';
import type { DadosDoPix } from '../../../logica/dinheiro/usePagamentoPix';
import { CarregandoPagina } from '../../padroes/Carregando';
import { ConfirmarPerigo } from '../../padroes/ConfirmarPerigo';
import { PaginaDeConta } from '../../padroes/PaginaDeConta';
import { ROTULOS, rotuloDoAviso } from '../../padroes/rotulos';
import { Vazio } from '../../padroes/Vazio';
import styles from './Dinheiro.module.scss';

// O PAGAMENTO PIX (/pagamento), no Carbon: o QR num Tile, o prazo quando ele importa, o "copia e
// cola" no CodeSnippet do Carbon (com o copiar dele), e a espera pelo pagamento. Pix Automático
// avisa ANTES do QR que pagar autoriza os débitos seguintes. Os outros estados (retomando, cartão
// em análise, mensalidade em atraso, não abriu, venceu, pago, sem tempo, sem conexão) são o vazio
// do Carbon com a saída de cada um. Em todos os da primeira assinatura há "Alterar plano ou
// pagamento" (04/10/2026): quem tem o PIX do banco com erro troca para o cartão, e quem escolheu
// os perfis errado corrige.

export const PagamentoPix: FC<{ x: DadosDoPix }> = ({ x }) => {
  if (x.estado === 'retomando' || x.estado === 'nada') return <CarregandoPagina descricao='Recuperando o seu pagamento' />;

  const a = x.alterar;
  const emCurso = !!a?.emCurso;
  // A saída de quem quer outro plano ou outra forma de pagamento, com a confirmação (o PIX gerado
  // deixa de valer). Na renovação não existe: a assinatura já está de pé.
  const alterar = a && <Button kind='ghost' size='md' onClick={a.pedir} disabled={emCurso}>Alterar plano ou pagamento</Button>;
  const confirmacao = a && (
    <ConfirmarPerigo aberto={a.aberto} perigo={false} rotulo='Assinatura' titulo='Alterar plano ou pagamento?' texto={a.texto}
      acao='Alterar' manter='Manter como está' emCurso={a.emCurso} aoConfirmar={a.alterar} aoFechar={a.fechar} />
  );

  const estado = (pictograma: Parameters<typeof Vazio>[0]['pictograma'], titulo: string, texto: string, acao?: React.ReactNode) => (
    <PaginaDeConta titulo='Pagamento'>
      <Vazio pictograma={pictograma} titulo={titulo} texto={texto} acao={acao} />
      {confirmacao}
    </PaginaDeConta>
  );

  switch (x.estado) {
    case 'cartaoEmAnalise':
      return estado(CreditCard, 'Pagamento em análise', 'A operadora do cartão está processando o débito, o que pode levar alguns minutos. Assim que for aprovado, o acesso PRO é liberado sozinho. Pode fechar esta tela e seguir usando o app.', (
        <>
          <Button kind='primary' size='md' onClick={x.voltarAoPainel}>Voltar aos perfis</Button>
          {alterar}
        </>
      ));
    case 'faturaEmAtraso':
      // A mensalidade em atraso no cartão ou no Pix Automático: paga-se na fatura da Asaas (outra
      // aba), e esta tela fica esperando o pagamento cair.
      return x.fatura ? estado(Receipt, x.fatura.titulo, x.fatura.texto, (
        <>
          <Button kind='primary' size='md' renderIcon={Launch} href={x.fatura.url} target='_blank' rel='noopener noreferrer'>Pagar a mensalidade</Button>
          <Button kind='ghost' size='md' onClick={x.voltarAoPainel}>Voltar aos perfis</Button>
        </>
      )) : null;
    case 'naoAbriu':
      // Pode ser só um instante da Asaas: a assinatura segue pendente, e nada se descarta sozinho.
      return estado(Wifi, 'Não deu para abrir o seu PIX agora', `A sua assinatura continua esperando o pagamento. Tente de novo em instantes${a ? ', ou altere o plano e a forma de pagamento' : ''}.`, (
        <>
          <Button kind='primary' size='md' onClick={x.abrirDeNovo} disabled={emCurso}>Tentar de novo</Button>
          {alterar}
        </>
      ));
    case 'venceu':
      return estado(Time, 'Este PIX venceu', 'Nada foi cobrado. Escolha de novo o plano e a forma de pagamento para gerar outro.', (
        emCurso
          ? <InlineLoading description='Preparando…' iconDescription={ROTULOS.carregando} />
          : <Button kind='primary' size='md' renderIcon={ArrowRight} onClick={a ? a.alterar : x.voltarAoPainel}>Escolher de novo</Button>
      ));
    case 'pago':
      return estado(Trophy, 'Bem-vindo ao Maestra PRO', 'Pagamento confirmado. Todos os recursos estão liberados. Levando você para lá…');
    case 'semTempo':
      return estado(Receipt, 'O pagamento ainda não foi confirmado', `Se você já pagou, o acesso libera sozinho quando o banco confirmar. Pode continuar esperando aqui${a ? ', ou alterar o plano e a forma de pagamento' : ''}.`, (
        <>
          <Button kind='primary' size='md' onClick={x.tentarDeNovo} disabled={emCurso}>Continuar esperando</Button>
          {alterar}
        </>
      ));
    case 'semConexao':
      return estado(Wifi, 'A conexão caiu', 'Confira a internet e tente de novo.',
        <Button kind='primary' size='md' onClick={x.tentarDeNovo}>Tentar de novo</Button>);
    default:
      break;
  }

  return (
    <PaginaDeConta titulo={x.automatico ? 'Pagamento por Pix Automático' : 'Pagamento PIX'} descricao='Escaneie o QR Code no app do banco ou copie o código.'>
      <Stack gap={6} className={styles.encolhe}>
        {x.automatico && (
          <InlineNotification kind='info' lowContrast hideCloseButton title='Esta é a última vez que você paga à mão'
            subtitle='Ao confirmar no app do banco, você autoriza a cobrança automática das próximas mensalidades, no mesmo valor e na mesma data. Pode cancelar quando quiser, aqui na conta ou pelo próprio banco.'
            statusIconDescription={rotuloDoAviso('info')} aria-label={ROTULOS.fecharAviso} className={styles.aviso} />
        )}
        <Tile className={styles.bloco}>
          <Stack gap={5}>
            {x.qr && <img src={x.qr} alt='QR Code do PIX' className={styles.qr} />}
            {x.prazo && <div><Tag type={x.prazo.expirado ? 'red' : 'warm-gray'} size='md'>{x.prazo.texto}</Tag></div>}
            <p className={styles.apoio}>
              {'O pagamento aparece no seu banco em nome de '}
              <strong>{RECEBEDOR.razaoSocial}</strong>
              {` · CNPJ ${RECEBEDOR.cnpj}`}
            </p>
          </Stack>
        </Tile>
        <div className={styles.encolhe}>
          <p className={styles.rotuloPequeno}>PIX copia e cola</p>
          <CodeSnippet type='single' feedback='Copiado' copyButtonDescription='Copiar o código PIX' aria-label='Código PIX copia e cola'
            showMoreText='Mostrar mais' showLessText='Mostrar menos'>
            {x.copiaECola}
          </CodeSnippet>
        </div>
        <InlineLoading description='Esperando o pagamento…' iconDescription={ROTULOS.carregando} />
        {/* O PIX do banco com erro, ou os perfis escolhidos errado: aqui é a saída. */}
        {alterar && <div>{alterar}</div>}
      </Stack>
      {confirmacao}
    </PaginaDeConta>
  );
};
